import { useState, useEffect, useCallback, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import _ from "lodash"

import config from "../../config"

import {
  fetchAppManifest,
  fetchMapLabels,
  fetchAppConfig,
  fetchMapDecorations,
  fetchSponsoredContent,
} from "../../services/venue"
import { VenueContext } from "./VenueContext"

import { useShortestPath } from "./modules/shortestPath/useShortestPath"
import { useSearch } from "./hooks/useSearch.ts"
import { useTheme } from "./hooks/useTheme.ts"

import { prepareGeolocationFeature } from "providers/geolocation/utils/prepareGeolocationFeature"
import { prepareMapDecorations } from "./utils/prepareFeature"
import { useGetImdf } from "./modules/data"

import {
  createGeolocationFeature,
  isLatLngQueryString,
} from "utils/geolocation"

import {
  prepareExtensionConfig,
  prepareMapConfig,
  prepareSearchConfig,
  prepareSponsoredContentConfig,
  prepareStepAutoplayConfig,
} from "./utils/prepareConfig"
import { prepareSponsoredContent } from "./utils/prepareEntry"

const LOCALE_CONFIG = {
  en: {
    localeKey: "en",
    name: "English",
    flagCode: "gb",
    localizedName: "English",
  },
  th: {
    localeKey: "th",
    name: "Thai",
    flagCode: "th",
    localizedName: "ไทย",
  },
  zh: {
    localeKey: "zh",
    name: "Chinese",
    flagCode: "cn",
    localizedName: "Chinese",
  },
  ja: {
    localeKey: "ja",
    name: "Japanese",
    flagCode: "jp",
    localizedName: "Japanese",
  },
}

const DEFAULT_LOCALE_SUPPORT_LIST = ["en", "th"]

export const defaultAppConfig = {
  map: {},
}

export const VenueProvider = ({ children }) => {
  /** State, Hooks */
  const device = config("device")
  const deviceType = /^kiosk/.test(device) ? "kiosk" : "mobile"

  const [appManifest, setAppManifest] = useState(null)
  /** 
   * Example Extension Configs
    {
      "extensions": {
          "app": [
            {
              "to": "promotions", // <-- path for registering route
              "component": "tourist-friendly/pages/promotions" // <-- component path for loading
            },
            ....
          ]
        }
      }
    }
  */

  const { data: imdfData, isFetched, refetch: refetchImdfData } = useGetImdf()

  const groupedLocationBySection = useMemo(() => {
    if (!isFetched) return
    const { units, kiosks } = imdfData
    const filteredUnits = units.filter(
      (unit) =>
        !!unit.properties?.section_id &&
        !["walkway"].includes(unit.properties.category)
    )
    const filteredKiosks = kiosks.filter(
      (kiosk) => !!kiosk.properties?.section_id
    )
    const locations = [...filteredUnits, ...filteredKiosks]
    return _.groupBy(locations, "properties.section_id")
  }, [imdfData, isFetched])

  const { data: appConfig, isFetched: isConfigFetched } = useQuery({
    queryKey: ["appConfig"],
    queryFn: async () => {
      try {
        const appConfig = await fetchAppConfig()
        return appConfig || defaultAppConfig
      } catch (err) {
        return defaultAppConfig
      }
    },
    initialData: defaultAppConfig,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const { data: sponsoredContent, isFetched: isSponsoredContentFetched } =
    useQuery({
      enabled: isFetched && isConfigFetched,
      queryKey: ["sponsoredContent"],
      queryFn: async () => {
        try {
          const sponsoredContents = await fetchSponsoredContent()
          const featureObj = _.get(imdfData, "featuresObj")
          const sponsoredContentConfig = prepareSponsoredContentConfig(
            appConfig,
            deviceType
          )
          return prepareSponsoredContent(
            sponsoredContents,
            sponsoredContentConfig,
            featureObj
          )
        } catch (err) {
          return {}
        }
      },
      initialData: {},
      retry: false,
      refetchOnWindowFocus: false,
    })

  const mapConfig = useMemo(
    () => prepareMapConfig(_.get(appConfig, "map")),
    [appConfig]
  )

  const stepAutoplayConfig = useMemo(
    () => prepareStepAutoplayConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const searchConfig = useMemo(
    () => prepareSearchConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const extensionConfig = useMemo(
    () => prepareExtensionConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const { data: labels } = useQuery({
    queryKey: ["labels"],
    queryFn: fetchMapLabels,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const { data: decorations, isFetched: isDecorationFetched } = useQuery({
    queryKey: ["elements"],
    queryFn: async () => {
      const decorationsData = await fetchMapDecorations()
      return prepareMapDecorations(decorationsData)
    },
    retry: false,
    refetchOnWindowFocus: false,
  })

  //correct locale properties is supported_locales
  const venueSupportedLocales = useMemo(
    () =>
      _.get(
        _.first(imdfData.venues),
        "properties.supported_locales",
        DEFAULT_LOCALE_SUPPORT_LIST
      ),
    [imdfData.venues]
  )

  const locales = useMemo(() => {
    try {
      return _(venueSupportedLocales)
        .map((locale) => _.get(LOCALE_CONFIG, locale))
        .compact()
        .value()
    } catch (error) {
      return _(DEFAULT_LOCALE_SUPPORT_LIST)
        .map((locale) => _.get(LOCALE_CONFIG, locale))
        .compact()
        .value()
    }
  }, [venueSupportedLocales])

  const themeName = config("themeName")
  const mapThemeName = config("mapThemeName")
  const { themeOptions, initVenueTheme, initMapTheme, mapTheme } = useTheme(
    themeName,
    mapThemeName
  )

  const { search, getSuggestedSearchItems } = useSearch(imdfData.features, {
    config: searchConfig,
    enable: isConfigFetched,
  })

  const graphOptions = useMemo(
    () => _.get(mapConfig, "prepare_steps", {}),
    [mapConfig]
  )

  const {
    findRoute,
    findElevatorPreferredRoute,
    relationshipGraphLoaded,
    transformStepsToGeometries,
    normalizeStepInRoute,
  } = useShortestPath(imdfData, {
    /**
     * ==== Example App Config ====
     * "map": {
     *   ... other configs ...
     *   "shortest_path": {
     *     "resolution": 0.0016
     *   },
     *   "prepare_steps": {
     *     "traversal": {
     *       "room": 100,
     *       "unenclosedarea": 100
     *     },
     *     "escalator": {
     *       "baseDistance": 100,
     *       "multiplier": 1
     *     }
     *     // ...others...
     *   }
     * }
     */
    ..._.get(mapConfig, "shortest_path", {}),
    enabled: isFetched,
    graphOptions,
  })

  const rootCategories = useMemo(
    () =>
      _.uniqBy(
        _.filter(
          imdfData.categories,
          (feature) => feature.properties.parent_id === null
        ),
        "id"
      ),
    [imdfData.categories]
  )

  const findFeatureById = useCallback(
    (id) => imdfData.features.find((f) => f.id === id),
    [imdfData]
  )

  const transformLocationToFeature = useCallback(
    (feature) => {
      return isLatLngQueryString(feature)
        ? prepareGeolocationFeature(
            createGeolocationFeature(feature, { name: "Your Location" }),
            {
              venues: imdfData.venues,
              levels: imdfData.levels,
              units: imdfData.units,
            }
          )
        : findFeatureById(feature) || null
    },
    [findFeatureById, imdfData.venues, imdfData.levels, imdfData.units]
  )

  const filterAmenitiesByCategory = useCallback(
    (category) => {
      return imdfData.amenities.filter(
        (amenityFeature) => amenityFeature.properties.category === category
      )
    },
    [imdfData.amenities]
  )

  const filterOccupantsByRootCategory = useCallback(
    (category) => {
      return imdfData.occupants.filter(
        ({ feature_type, properties }) =>
          feature_type === "occupant" &&
          properties.anchor &&
          (category === undefined ||
            properties.local_parent_categories?.[0]?.id === category)
      )
    },
    [imdfData.occupants]
  )

  const isMultiOrdinalVenue = useMemo(() => {
    return _.uniqBy(imdfData.levels, "properties.ordinal").length > 1
  }, [imdfData.levels])

  const isMultiVenueProject = useMemo(() => {
    return imdfData.venues.length > 1
  }, [imdfData.venues])

  // TODO: Find and Implement default Venue
  const defaultVenue = useMemo(() => _.first(imdfData.venues), [imdfData])

  const initVenue = useCallback(async () => {
    const manifest = await fetchAppManifest()
    setAppManifest(manifest)

    initVenueTheme()
    initMapTheme()
  }, [initVenueTheme, initMapTheme])

  /**
   * Side Effect when manifest is updated
   */
  useEffect(() => {
    // Set favicon
    const favicon = appManifest?.icons?.find(
      (icon) => icon.type === "image/x-icon"
    )
    if (favicon) {
      document.getElementById("favicon").href = favicon.src
    }
  }, [appManifest])

  // fetch ข้อมูลเริ่มต้นลง feature state และ theme
  useEffect(() => {
    try {
      initVenue()
    } catch (err) {
      throw err
    }
  }, [initVenue])

  const value = {
    ...imdfData,
    groupedLocationBySection,
    labels,
    decorations,
    themeOptions,
    appManifest,
    mapTheme,
    locales,
    rootCategories,
    findRoute,
    isMultiOrdinalVenue,
    isMultiVenueProject,
    findElevatorPreferredRoute,
    appConfig,
    stepAutoplayConfig,
    extensionConfig,
    sponsoredContent,
    defaultVenue,
    // Flag
    relationshipGraphLoaded,
    dataLoaded:
      isFetched &&
      isDecorationFetched &&
      isConfigFetched &&
      isSponsoredContentFetched,
    mapConfig,
    // functions
    refetchImdfData,
    findFeatureById,
    search,
    filterAmenitiesByCategory,
    filterOccupantsByRootCategory,
    transformLocationToFeature,
    transformStepsToGeometries,
    normalizeStepInRoute,
    getSuggestedSearchItems,
  }

  return <VenueContext.Provider value={value}>{children}</VenueContext.Provider>
}
